import { Show, useRecordContext, useGetList, EditButton, usePermissions } from 'react-admin';
import ActivityPanel from '../components/ActivityPanel';
import TagsEditor from '../components/TagsEditor';
import EntityTasksPanel from '../tasks/EntityTasksPanel';
import ShluchimEligibility from '../website/ShluchimEligibility';
import {
  Box, Typography, Card, CardContent, Stack, Avatar, Chip,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import PhoneIcon      from '@mui/icons-material/Phone';
import EmailIcon      from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';
import { card, tbl, NAVY, GOLD, ORDER_STATUS, PAYMENT_STATUS, PAYMENT_METHOD_LABELS, badgeStyle, clickableA11y } from '../styles/ds';

const fmt_ils = v => `₪${Number(v || 0).toLocaleString('he-IL', { minimumFractionDigits: 0 })}`;
const fmt_usd = v => `$${Number(v || 0).toLocaleString('en-US',  { minimumFractionDigits: 0 })}`;
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('he-IL') : '—';

const CustomerDetail = () => {
  const record   = useRecordContext();
  const navigate = useNavigate();
  const { permissions } = usePermissions();

  const { data: orders = [], isLoading } = useGetList('orders', {
    filter: { customer_id: record?.id },
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'id', order: 'DESC' },
  });

  // All payments for this customer (via orders)
  const { data: payments = [], isLoading: paymentsLoading } = useGetList('payments', {
    filter: { customer_id: record?.id },
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'id', order: 'DESC' },
  });

  if (!record) return null;

  const initials  = `${record.first_name?.[0] ?? ''}${record.last_name?.[0] ?? ''}`.toUpperCase();
  const fullName  = `${record.first_name ?? ''} ${record.last_name ?? ''}`.trim();
  const totalIls  = orders.filter(o => o.status !== 'cancelled').reduce((s, o) => s + Number(o.total_price_ils || 0), 0);
  const totalUsd  = orders.filter(o => o.status !== 'cancelled').reduce((s, o) => s + Number(o.total_price_usd || 0), 0);

  return (
    <Box className="crm-page crm-detail-content" p={3}>
      {/* Header */}
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 64, height: 64, fontSize: 22, fontWeight: 700, backgroundColor: NAVY, color: GOLD }}>
            {initials}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700} color="primary.main">{fullName}</Typography>
            {record.country && (
              <Typography variant="body2" color="text.secondary">
                {[record.city, record.country].filter(Boolean).join(', ')}
              </Typography>
            )}
          </Box>
        </Stack>
        <EditButton label="ערוך" />
      </Stack>

      {/* Contact info */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Typography fontWeight={700} color="primary.main" mb={2}>פרטי יצירת קשר</Typography>
          <Stack spacing={1.2}>
            {record.phone && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <PhoneIcon sx={{ color: GOLD, fontSize: 18 }} />
                <Typography>{record.phone}</Typography>
              </Stack>
            )}
            {record.email && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <EmailIcon sx={{ color: GOLD, fontSize: 18 }} />
                <Typography>{record.email}</Typography>
              </Stack>
            )}
            {(record.city || record.country) && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <LocationOnIcon sx={{ color: GOLD, fontSize: 18 }} />
                <Typography>{[record.city, record.country].filter(Boolean).join(', ')}</Typography>
              </Stack>
            )}
            {record.address && (
              <Typography variant="body2" color="text.secondary" sx={{ pr: 4 }}>{record.address}</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Tags */}
      <TagsEditor customerId={record.id} />

      {/* Website: Shluchim coupon eligibility (WD-006, admin only) */}
      {permissions === 'admin' && <ShluchimEligibility customer={record} />}

      {/* Dedicated tasks for this customer */}
      <EntityTasksPanel entityType="customer" entityId={record.id} />

      {/* Activities, notes, tasks and follow-ups */}
      <ActivityPanel customerId={record.id} isAdmin={permissions === 'admin'} />

      {/* Stats */}
      <Stack className="crm-wrap crm-stat-row" direction="row" spacing={2} mb={3} flexWrap="wrap">
        <Card sx={{ borderRadius: 2, minWidth: 140 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">הזמנות</Typography>
            <Typography fontWeight={700} fontSize={24} color="primary.main">{orders.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, minWidth: 160 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">סה"כ רכישות ₪</Typography>
            <Typography fontWeight={700} fontSize={20} color="#2e7d32">{fmt_ils(totalIls)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, minWidth: 160 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">סה"כ רכישות $</Typography>
            <Typography fontWeight={700} fontSize={20} color="#1565c0">{fmt_usd(totalUsd)}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Order history */}
      <Typography variant="h6" fontWeight={700} color="primary.main" mb={2}>היסטוריית הזמנות</Typography>

      {isLoading ? (
        <Typography color="text.secondary">טוען...</Typography>
      ) : orders.length === 0 ? (
        <Typography color="text.secondary">אין הזמנות עדיין</Typography>
      ) : (
        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell align="center">סטטוס</TableCell>
                <TableCell align="right">סה"כ ₪</TableCell>
                <TableCell align="right">סה"כ $</TableCell>
                <TableCell align="center">תאריך</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order, i) => (
                <TableRow key={order.id} onClick={() => navigate(`/orders/${order.id}`)}
                  {...clickableA11y(() => navigate(`/orders/${order.id}`), { ariaLabel: `פתיחת הזמנה מספר ${order.id}` })}
                  sx={tbl.bodyRow(i)}>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>#{order.id}</TableCell>
                  <TableCell align="center">
                    <span style={badgeStyle(ORDER_STATUS, order.status)}>
                      {ORDER_STATUS[order.status]?.label || order.status}
                    </span>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt_ils(order.total_price_ils)}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmt_usd(order.total_price_usd)}</TableCell>
                  <TableCell align="center" sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {fmtDate(order.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></Box>
        </Card>
      )}

      {/* ── Payment history ── */}
      <Typography variant="h6" fontWeight={700} color="primary.main" mt={3} mb={2}>
        היסטוריית תשלומים
      </Typography>

      {paymentsLoading ? (
        <Typography color="text.secondary">טוען...</Typography>
      ) : payments.length === 0 ? (
        <Typography color="text.secondary">אין תשלומים עדיין</Typography>
      ) : (
        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell align="center">הזמנה</TableCell>
                <TableCell align="right">סכום</TableCell>
                <TableCell>אמצעי תשלום</TableCell>
                <TableCell align="center">סטטוס</TableCell>
                <TableCell align="center">תאריך</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((p, i) => {
                const fmtAmt = p.currency === 'USD'
                  ? `$${Number(p.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                  : `₪${Number(p.amount || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`;
                return (
                  <TableRow key={p.id} onClick={() => navigate(`/orders/${p.order_id}`)}
                    {...clickableA11y(() => navigate(`/orders/${p.order_id}`), { ariaLabel: `פתיחת תשלום להזמנה מספר ${p.order_id}` })}
                    sx={tbl.bodyRow(i)}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 13 }}>#{p.id}</TableCell>
                    <TableCell align="center">
                      <Chip label={`הזמנה #${p.order_id}`} size="small"
                        sx={{ backgroundColor: 'rgba(13,29,62,0.08)', fontSize: 11 }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>{fmtAmt}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method || '—'}</TableCell>
                    <TableCell align="center">
                      <span style={badgeStyle(PAYMENT_STATUS, p.status)}>
                        {PAYMENT_STATUS[p.status]?.label || p.status}
                      </span>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>{fmtDate(p.created_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table></Box>
        </Card>
      )}
    </Box>
  );
};

const CustomerShow = () => (
  <Show title=" " actions={false}>
    <CustomerDetail />
  </Show>
);

export default CustomerShow;
